import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { ModalBodyWrapper } from 'src/components/form/form/ModalBodyWrapper';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import { useGetDocumentInternalAuditResultById } from 'src/hooks/queries';
import DocumentAssessmentResultForm from 'src/pages/assessments/forms/DocumentAssessmentResultForm';
import type { DocumentAssessmentResultFormDataFields } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import { defaultValues } from 'src/pages/assessments/forms/documentAssessmentResultSchema';
import { isResultFormReadOnly } from 'src/pages/assessments/forms/resultFormReadOnly';
import type { ObjectWithContributors } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useMultiParentFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertDocumentInternalAuditResult } from '@/hooks/mutations/internal-audit-result/useInsertDocumentInternalAuditResult';
import { useUpdateDocumentInternalAuditResult } from '@/hooks/mutations/internal-audit-result/useUpdateDocumentInternalAuditResult';
import { internalAuditReportResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  isModalForm: boolean;
  parentId?: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  showAssessmentSelector?: boolean;
  documentIds?: string[];
  header?: string;
};

const ConnectedDocumentInternalAuditResultForm: FC<Props> = ({
  readonly,
  parentId,
  assessedItem,
  id,
  onDismiss,
  beforeFieldsSlot,
  showAssessmentSelector,
  navigateToResults,
  isModalForm,
  documentIds,
  header,
}) => {
  const navigate = useNavigate();
  const { data } = useGetDocumentInternalAuditResultById({
    queryArgs: { id: id ?? '' },
    shouldSkip: !id,
  });
  const documentInternalAuditResult = data?.document_internal_audit_result?.[0];
  const {
    hasPermission: canUpdateDocumentAssessmentResult,
    loading: isLoadingCanUpdateDocumentAssessmentResult,
  } = useHasPermissionQuery(
    'update:document_internal_audit_result',
    assessedItem
  );

  const { updateFiles } = useMultiParentFileUpdate();
  const { insertDocumentInternalAuditResult } =
    useInsertDocumentInternalAuditResult();
  const { updateDocumentInternalAuditResult } =
    useUpdateDocumentInternalAuditResult();

  documentIds = documentIds ?? [];
  const parentDocument = documentInternalAuditResult?.parents.find(
    (p) => p.document
  );
  if (parentDocument?.document?.Id) {
    documentIds.push(parentDocument.document.Id);
  } else if (assessedItem?.Id) {
    documentIds.push(assessedItem.Id);
  }

  const onSave = async (values: DocumentAssessmentResultFormDataFields) => {
    const { files } = values;
    const documentAssessmentResultIds: string[] = [];
    if (documentInternalAuditResult) {
      const result = await updateDocumentInternalAuditResult({
        Id: documentInternalAuditResult.Id,
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (result.update_document_internal_audit_result?.affected_rows === 0) {
        throw new Error('Document result update failed');
      }
      documentAssessmentResultIds.push(id!);
    } else {
      const result = await insertDocumentInternalAuditResult({
        InternalAuditReportId: parentId!,
        DocumentIds: values?.DocumentIds?.map((r) => r.value) ?? [],
        Rating: values.Rating,
        Rationale: values.Rationale,
        TestDate: values.TestDate,
        CustomAttributeData: values.CustomAttributeData ?? null,
      });

      if (!result.insertChildDocumentInternalAuditResult?.Ids?.length) {
        throw new Error('Document result id is missing');
      }
      documentAssessmentResultIds.push(
        ...result.insertChildDocumentInternalAuditResult.Ids
      );
    }

    await updateFiles({
      parentIds: documentAssessmentResultIds,
      parentType: Parent_Type_Enum.DocumentInternalAuditResult,
      selectedFiles: files,
      originalFiles:
        documentInternalAuditResult?.files.map((f) => f.file) ?? [],
    });
    if (navigateToResults && parentId) {
      navigate(internalAuditReportResultsUrl(parentId));
    }
  };

  return (
    <DocumentAssessmentResultForm
      header={header}
      defaultValues={{
        ...defaultValues,
        DocumentIds: documentIds.map((c) => ({ value: c })),
        InternalAuditReportId: parentId,
      }}
      values={
        documentInternalAuditResult
          ? {
              Rating: documentInternalAuditResult?.Rating ?? 1,
              Rationale: documentInternalAuditResult?.Rationale ?? '',
              InternalAuditReportId: parentId,
              DocumentIds: documentIds.map((c) => ({ value: c })),
              TestDate: documentInternalAuditResult?.TestDate,
              CustomAttributeData:
                documentInternalAuditResult?.CustomAttributeData,
              files:
                documentInternalAuditResult?.files.map((f) => f.file) ?? [],
            }
          : undefined
      }
      onDismiss={onDismiss}
      onSave={onSave}
      readOnly={isResultFormReadOnly({
        readonly,
        permissionLoading: isLoadingCanUpdateDocumentAssessmentResult,
        canUpdate: canUpdateDocumentAssessmentResult,
      })}
      renderTemplate={(renderProps) =>
        isModalForm ? (
          <ModalBodyWrapper {...renderProps} />
        ) : (
          <PageWrapper {...renderProps} />
        )
      }
      disableDocumentSelector={id != undefined}
      beforeFieldsSlot={beforeFieldsSlot}
      showSelector={
        showAssessmentSelector ? 'internal_audit_report' : undefined
      }
      assessmentMode={'internal_audit_report'}
    />
  );
};

export default ConnectedDocumentInternalAuditResultForm;
